import React, { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { useProtocolData } from "../../hooks/protocoldata/useProtocolData";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";

export const ProtocolTreeView = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const { token } = useAuthContext();
  const { conditions } = useProtocolData(token);

  const data = conditions;

  const [expandedParent, setExpandedParent] = useState(null); // Track the currently expanded parent
  const [selectedChild, setSelectedChild] = useState(null); // Track the currently selected child

  const toggleNode = (parent) => {
    setExpandedParent((prev) => (prev === parent ? null : parent)); // Toggle the parent node
    setSelectedChild(null);
  };

  const handleNodeClick = (nodeId, isParent) => {
    if (isParent) {
      toggleNode(nodeId);
      navigate(`/exercise/${nodeId}`);
    } else {
      const [parent, child] = nodeId.split("/");

      // Collapse other parents and select the child
      setExpandedParent(parent);
      setSelectedChild(nodeId);

      if (child) {
        navigate(`/exercise/${parent}/${child}`);
      } else {
        navigate(`/exercise/${parent}`);
      }
    }
  };

  // Sort the data by area name
  const sortedData = Object.entries(data).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  return (
    <Box sx={{ margin: 3 }}>
      <Box
        sx={{
          padding: "10px",
          maxHeight: "400px",
          overflowY: "auto",
          border: `1px solid ${
            darkMode ? getColors.appBarBorderDark : getColors.appBarBorderLight
          }`,
          borderRadius: "5px",
        }}
      >
        {sortedData.map(([parent, children]) => (
          <Box key={parent} sx={{ mb: 2 }}>
            {/* Parent Node */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                mb: 1,
              }}
              onClick={() => handleNodeClick(parent, true)}
            >
              {expandedParent === parent ? (
                <ExpandMoreIcon sx={{ mr: 1 }} />
              ) : (
                <ChevronRightIcon sx={{ mr: 1 }} />
              )}
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                  fontWeight: 600,
                }}
              >
                {parent}
              </Typography>
            </Box>

            {/* Child Nodes */}
            {expandedParent === parent &&
              children.map((child) => (
                <Box
                  key={child}
                  sx={{
                    pl: 2,
                    mx: 2,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    mb: 1,
                    backgroundColor:
                      selectedChild === `${parent}/${child}`
                        ? getColors.primaryMain
                        : "transparent",
                    borderRadius: "5px",
                  }}
                  onClick={() => handleNodeClick(`${parent}/${child}`, false)}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      py: "6px",
                      color:
                        selectedChild === `${parent}/${child}`
                          ? "#ffffff"
                          : darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                    }}
                  >
                    {child}
                  </Typography>
                </Box>
              ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
